import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import useStyles from '../../theme/useStyles';
import Icon, { iconFor } from './Icon';
import { hexToRgba, lighten } from '../../theme/tokens';

// A module tile on the dashboard grid: white card, the module's colour only
// as the tint behind its icon and a rule down the left.
export interface CardProps {
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  highlighted?: boolean;
  highlightLabel?: string;
  /** Kept for callers; the tile no longer paints its background with it. */
  backgroundColor?: string;
  /** The module's accent — tints the icon chip and the left rule. */
  accent?: string;
  children?: ReactNode;
  style?: CSSProperties;
  isDarkMode?: boolean;
}

const Card = ({
  icon,
  title,
  description,
  onClick,
  highlighted = false,
  highlightLabel = 'Last Visited',
  backgroundColor,
  accent,
  children,
  style,
  isDarkMode
}: CardProps) => {
  const s = useStyles(isDarkMode);
  const t = s.t;

  const tint = accent || t.primary;
  const tintText = t.isDarkMode ? lighten(tint, 0.45) : tint;

  return (
    <div
      onClick={onClick}
      style={{
        ...s.card,
        backgroundColor: t.bgMain,
        borderLeft: `3px solid ${tint}`,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        ...(highlighted ? { boxShadow: `0 0 0 2px ${hexToRgba(tint, 0.35)}, ${t.shadowRaised}` } : {}),
        ...style
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = t.shadowRaised; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = highlighted ? `0 0 0 2px ${hexToRgba(tint, 0.35)}, ${t.shadowRaised}` : (t.shadowCard as string); }}
    >
      {highlighted && (
        <div style={{
          position: 'absolute', top: '10px', right: '10px',
          backgroundColor: hexToRgba(tint, t.isDarkMode ? 0.25 : 0.12), color: tintText,
          padding: '2px 8px', borderRadius: t.radius.pill,
          fontSize: t.fontSize.xs, fontWeight: t.fontWeight.bold
        }}>
          {highlightLabel}
        </div>
      )}

      {(icon || title) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: t.spacing.md }}>
          {icon && (
            <div className="notranslate" translate="no" style={{
              backgroundColor: hexToRgba(tint, t.isDarkMode ? 0.22 : 0.12),
              color: tintText,
              borderRadius: t.radius.md,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '44px', height: '44px', flexShrink: 0
            }}>
              {typeof icon === 'string' ? <Icon name={iconFor(icon)} size={22} /> : icon}
            </div>
          )}
          <div style={{ textAlign: 'left' }}>
            {title && <h3 style={{ color: t.textMain, margin: '0 0 3px 0', fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold }}>{title}</h3>}
            {description && <p style={{ color: t.textMuted, margin: 0, fontSize: t.fontSize.sm, lineHeight: 1.4 }}>{description}</p>}
          </div>
        </div>
      )}

      {children}
    </div>
  );
};

export default Card;
